import {
  Box3,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  EdgesGeometry,
  Frustum,
  LineBasicMaterial,
  LineSegments,
  Points,
  Scene,
  Vector3
} from 'three';
import { isDevMode } from '../../electron-modules';
import { PointCloudMaterial } from '../rendering';
import { PointCloudManager } from '.';
import { closestPowerOfTwo, mergeBoundingBoxes, randomHexColor, toVector2 } from '../utilities';
import GPUPicker from '../gpu-picker';

/** Base class for all pointcloud nodes */
export class PointCloudNode {
  public cloud: PointCloud
  public parent: PointCloudNode

  public pointsScene: Points;
  public pointsPicker: Points;
  protected boundingBoxMesh: LineSegments;
  public geometry: BufferGeometry;
  protected _boundingBox: Box3;

  public name: string;
  public nodeSize: number;
  public edgeSize = new Vector3();
  protected data;
  protected nodeCenter: Vector3;
  public numPoints = 0;
  public children: PointCloudNode[] = []
  public density: number;

  public visible = false;
  public loaded = false;
  public loading = false;

  get scene(): Scene {
    return this.cloud.scene;
  }

  get picker(): GPUPicker {
    return this.cloud.picker;
  }

  /** Bounding box in viewer coordinates */
  get boundingBox(): Box3 {
    if (!this._boundingBox) {
      const size = new Vector3()
        .add(this.edgeSize)
        .divideScalar(2);

      const min = new Vector3()
        .add(this.center)
        .sub(size);

      const max = new Vector3()
        .add(this.center)
        .add(size);

      this._boundingBox = new Box3(min, max);
    }

    return this._boundingBox;
  }

  get shift() {
    return new Vector3();
  }

  /** Node center in viewer coordinates */
  get center() {
    return new Vector3()
      .add(this.nodeCenter)
      .add(this.shift);
  }

  /** Bounding sphere radius */
  get radius() {
    return Math.sqrt(3) * (this.nodeSize / 2.0);
  }

  get isGeometryNode() {
    return !this.pointsScene;
  }

  get isTreeNode() {
    return !!this.pointsScene;
  }

  addChildNode(node) {
    this.children.push(node);
  }

  clearChildNodes() {
    this.children.forEach(child => child = null);
    this.children = [];
  }

  removeChildNode(node) {
    let index = this.children.indexOf(node);
    if (index !== -1) {
      this.children.splice(index, 1);
    }

    node.clearChildNodes();
    node = null;
  }

  removeFromScene(item) {
    if (!item) return;
    this.scene.remove(item);
  }

  disposeGeometry(item) {
    if (!item) {
      return;
    }

    item.geometry.dispose();
    item.material.dispose();
  }

  dispose() {
    this.removeFromScene(this.pointsScene);
    this.disposeGeometry(this.pointsScene);
    this.pointsScene = null;

    this.picker.remove(this.pointsPicker);
    this.disposeGeometry(this.pointsPicker);
    this.pointsPicker = null;

    if (this.boundingBoxMesh) {
      this.removeFromScene(this.boundingBoxMesh);
      this.disposeGeometry(this.boundingBoxMesh);
      this.boundingBoxMesh = null;
    }

    if (this.geometry) {
      this.geometry.dispose();
    }

    this.geometry = null;
    this.loaded = false;
    this.loading = false;

    // console.log(`Dispose node: ${this.name}`);
  }

  setVisibility(visible: boolean, boundsVisible: boolean) {
    if (this.pointsPicker) {
      this.pointsPicker.visible = visible;
    }

    if (this.pointsScene) {
      this.pointsScene.visible = visible;
    }

    if (this.boundingBoxMesh) {
      this.boundingBoxMesh.visible = boundsVisible;
    }

    this.visible = visible;
  }

  setGeometry(data) {
    let geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(data.positions, 3));
    geometry.setAttribute('intensity', new BufferAttribute(data.intensity, 1));
    geometry.setAttribute('classification', new BufferAttribute(data.classification, 1));
    geometry.setAttribute('rgb', new BufferAttribute(data.colors, 3));
    geometry.setAttribute('gpu_index', new BufferAttribute(data.gpu_index, 1));
    //geometry.computeBoundingBox();

    this.geometry = geometry;
  }

  addToScene() {
    let pointsScene = new Points(this.geometry, this.cloud.materialScene);
    pointsScene.visible = false;

    let pointsPicker = new Points(this.geometry, this.cloud.materialPicker);
    pointsPicker.visible = false;

    let boundingBox = generateEdgeMesh(this.boundingBox);

    this.addCloudData(pointsPicker, pointsScene, boundingBox);
  }

  addCloudData(pointsPicker: Points, pointsScene: Points, boundingBox: LineSegments) {
    // Add our global shift to line up with previous data
    pointsScene.position.copy(this.shift);
    pointsPicker.position.copy(this.shift);

    // Add our new objects
    this.pointsPicker = pointsPicker;
    this.pointsScene = pointsScene;

    this.scene.add(this.pointsScene);
    this.picker.add(this.pointsPicker);

    if (boundingBox) {
      // Bounding box is for dev mode only
      boundingBox.position.copy(this.center);
      this.boundingBoxMesh = boundingBox;
      this.scene.add(this.boundingBoxMesh);
    }
  }


  isVisible(frustum: Frustum) {
    // Ignore nodes based on our view frustrum
    if (!frustum.intersectsBox(this.boundingBox)) {
      return false;
    }

    const handler = this.cloud.pointclouds;
    const controls = handler.viewer.controls;
    const drawDistance = handler.drawDistance;
    const cameraPosition = controls.getCameraTarget();

    // Ignore nodes based on distance slider
    const bounds2D = toVector2(this.center);
    const camera2D = toVector2(cameraPosition);
    const nodeDistance = bounds2D.distanceTo(camera2D);
    if (nodeDistance > (this.radius + drawDistance)) {
      return false;
    }

    return true;
  }
}

/** Base class for all pointclouds */
export class PointCloud {
  public uniqueClassifications: Set<number>;
  private readonly rgbArray = [];
  public maxColor = null;
  public intensityRange = null;
  public identifier = null;
  public error = false;
  protected enabled = true;
  public visible = true;
  private name: string;
  public materialScene: PointCloudMaterial = null;
  public materialPicker: PointCloudMaterial = null;
  public materials: PointCloudMaterial[];
  public root: PointCloudNode = null;
  public pointclouds: PointCloudManager;
  protected _tightBoundingBox: Box3 = null;

  constructor(pointclouds: PointCloudManager) {
    this.pointclouds = pointclouds;
    this.uniqueClassifications = new Set();

    this.materialScene = new PointCloudMaterial(this.viewer,
      this.pointclouds.materialDefaults, false);

    this.materialPicker = new PointCloudMaterial(this.viewer,
      this.pointclouds.materialDefaults, true);

    this.materials = [
      this.materialScene,
      this.materialPicker
    ];
  }

  get shift() {
    return new Vector3();
  }

  get size() {
    return 0;
  }

  get viewer() {
    return this.pointclouds.viewer;
  }

  get scene() {
    return this.pointclouds.scene;
  }

  get picker() {
    return this.viewer.gpuPickers.default;
  }

  /** Get bounding box with global shift applied */
  get tightBoundingBox(): Box3 {
    let bbox = this._tightBoundingBox;
    if (!bbox) return null;

    return new Box3(
      new Vector3().add(bbox.min).add(this.shift),
      new Vector3().add(bbox.max).add(this.shift)
    );
  }

  setName(name) {
    this.name = name;
  }

  setState(state) {
    this.enabled = state;
  }

  setVisibility(visible) {
    const changed = this.visible !== visible;
    this.visible = visible;
    if (changed) {
      this.pointclouds.updateMinimapExtent();
    }
  }

  setIdentifier(identifier) {
    // console.log(`Added pointcloud [id = ${identifier}]`);
    identifier = identifier === undefined ? null : identifier;
    this.identifier = identifier;
  }

  destroy() {
    this.setState(false);
    this.disposeNodes();
  }

  disposeNodes() {
    const nodes = this.getVisibleNodes();
    nodes.forEach(node => {
      node.visible = false;
      node.dispose();
    });
  }

  getVisibleNodes(condition?) : PointCloudNode[] {
    console.warn("Not implemented");
    return [];
  }

  updateBoundingBox(boundingBox: Box3) {
    let combined = mergeBoundingBoxes([
      this._tightBoundingBox,
      boundingBox
    ]);

    this._tightBoundingBox = combined;
  }

  updateIntensityRange(intensityMin, intensityMax) {
    if (this.intensityRange === null) {
      this.intensityRange = {
        min: intensityMin,
        max: intensityMax
      };
    }

    this.intensityRange.min = Math.min(intensityMin, this.intensityRange.min);
    this.intensityRange.max = Math.max(intensityMax, this.intensityRange.max);
  }

  updateColorLimits(rgbMax) {
    let closestValue = closestPowerOfTwo(rgbMax);
    this.rgbArray.push(closestValue);
    this.rgbArray.sort();

    let arrMax = Math.max(...this.rgbArray);
    let arrMin = Math.min(...this.rgbArray);
    let diff = arrMax - arrMin;
    if (diff === 0) {
      this.maxColor = arrMax;
      return;
    }

    // Grab the middle value instead
    let midpoint = this.rgbArray.length / 2.0;
    midpoint = Math.ceil(midpoint);
    this.maxColor = this.rgbArray[midpoint];
  }

  updateClassificationFromHistogram(histogram) {
    let uniqueClassifications = [];

    histogram.forEach((count, index) => {
      if (count === 0) return;
      uniqueClassifications.push(index);
    });

    this.updateClassifications(uniqueClassifications);
  }

  updateClassifications(classifications: number[]) {
    classifications.forEach(item => {
      this.uniqueClassifications.add(item);
    });
  }


}

const generateEdgeMesh = (boundingBox: Box3) => {
  if (!isDevMode) return null;

  const size = new Vector3()
    .add(boundingBox.max)
    .sub(boundingBox.min);

  const color = randomHexColor();
  const boxGeometry = new BoxGeometry(size.x, size.y, size.z);
  const edgeGeometry = new EdgesGeometry(boxGeometry);
  const boxMaterial = new LineBasicMaterial({color});
  const mesh = new LineSegments(edgeGeometry, boxMaterial);
  mesh.visible = false;

  return mesh;
};

export const getClassificationHistogram = (classifications: number[]) => {
  const histogram = new Array(256).fill(0);

  classifications.forEach(classification => {
    histogram[classification] += 1;
  });

  return histogram;
};
